// TODO: replace implementation of lodash's get()
import get from 'lodash/get';

import MultiAnalytics from './MultiAnalytics';
import brands from './constants/brands';
import trackerIds from './constants/trackerIds';

import { parseQueryString } from '@zg-rentals/string-utils';

let instance: MultiAnalyticsModule | null = null;

/*
 * MultiAnalyticsModule
 *
 * usage example:
 * const ma = new MultiAnalyticsModule().init({
        brand,
        isProd: false,
    }).getMa();
 */

// To test GA on the sherman test server
// add query param ?enableGA=true
let overrideDryModeToBe = false;
let shouldOverrideDryMode = false;

type trackers = {
  dsp: string;
  gtm: string;
  ua: string;
  comscore: string;
};

export type MultiAnalyticsSettings = {
  brand: string;
  isProd: boolean;
  drymode: boolean;
  isWebPerformanceTest: boolean;
  ZRM_ENABLE_GA: boolean;
};

export default class MultiAnalyticsModule {
  brand: string = 'zillow';
  isProd: boolean = false;
  drymode: boolean = false;
  isWebPerformanceTest: boolean = false;
  trackersInUse: trackers = {
    dsp: '',
    gtm: '',
    ua: '',
    comscore: '',
  };
  ma: MultiAnalytics | null = null;

  static initialize(initialSettings: MultiAnalyticsSettings) {
    instance = new MultiAnalyticsModule(initialSettings);
    return instance;
  }

  constructor({
    brand = 'zillow',
    isProd = false,
    drymode = false,
    isWebPerformanceTest = false,
    ZRM_ENABLE_GA = false,
  }: MultiAnalyticsSettings) {
    this.brand = brand;
    this.isProd = isProd;
    this.drymode = drymode;
    this.isWebPerformanceTest = isWebPerformanceTest;
    this.trackersInUse = {
      dsp: '',
      gtm: '',
      ua: '',
      comscore: '',
    };

    if (!brand) {
      // eslint-disable-next-line no-console
      console.error('ERROR@ MultiAnalytics: brand must be set');
      return;
    }

    const ids: Set<string> = new Set();

    if (typeof window !== 'undefined') {
      const { search } = window.document.location;
      const { enableGA } = parseQueryString(search.toString());

      if (ZRM_ENABLE_GA || enableGA === 'true') {
        overrideDryModeToBe = false;
        shouldOverrideDryMode = true;
      }
    }

    // add gtm based in env
    if (!isWebPerformanceTest) {
      if (this.isProd) {
        const dsp = get(trackerIds, 'ids.dsp.prod');
        const gtm = get(trackerIds, 'ids.gtm.prod');
        const comscore = get(trackerIds, 'ids.comscore.prod');

        ids.add(dsp);
        this.trackersInUse.dsp = dsp;

        ids.add(gtm);
        this.trackersInUse.gtm = gtm;

        ids.add(comscore);
        this.trackersInUse.comscore = comscore;

        switch (brand) {
          case brands.zillow: {
            const ua = trackerIds.ids.ua.zrm;
            ids.add(ua);
            this.trackersInUse.ua = ua;
            break;
          }
          case brands.hotpads: {
            const ua = trackerIds.ids.ua.hp;
            ids.add(ua);
            this.trackersInUse.ua = ua;
            break;
          }
          case brands.trulia: {
            const ua = trackerIds.ids.ua.multibrand;
            ids.add(ua);
            this.trackersInUse.ua = ua;
            break;
          }
          default:
            // eslint-disable-next-line no-console
            console.info('No case for', brand);
            break;
        }
      } else {
        const dsp = get(trackerIds, 'ids.dsp.dev');
        const gtm = get(trackerIds, 'ids.gtm.dev');

        ids.add(dsp);
        this.trackersInUse.dsp = dsp;

        ids.add(gtm);
        this.trackersInUse.gtm = gtm;
      }
    }

    if (shouldOverrideDryMode && overrideDryModeToBe === false) {
      // NOTE: mocking uaZrm as test
      this.testZRM(ids);
    }

    // const logger = global.plainLogger.child('multiAnalyticsModule');

    this.ma = new MultiAnalytics({
      ids: [...ids],
      drymode: shouldOverrideDryMode && overrideDryModeToBe !== null ? overrideDryModeToBe : drymode,
      // logger,
      anonymousId: '',
    });
  }

  static getInstance() {
    if (!instance) {
      // eslint-disable-next-line no-console
      console.error('ERROR@MultiAnalytics: no instance created');
    }

    return instance;
  }

  testZRM(ids: Set<string>) {
    const ua = trackerIds.ids.ua.zrm;

    ids.add(ua);
    this.trackersInUse.ua = ua;
  }

  getMa() {
    return this.ma;
  }

  getTrackerIds() {
    return this.trackersInUse;
  }
}
